import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { Subscription } from 'rxjs/internal/Subscription';
import { DataTypeComponent } from '../../../data-type-component';
import { DateRangeDTO, FieldConfigDTO } from '../../../../../../system/system-dtos';
import { MyErrorStateMatcher, ValidatorConfig } from '../../../../../../common/field-validation/my-error-state-matcher';
import { UiValidator } from '../../../../../../common/field-validation/ui-validator.dto';
import { SystemService } from '../../../../../../system/system.service';

@Component({
  selector: 'app-form-field-date-range',
  templateUrl: './date-range-form-field.component.html',
  styleUrls: ['./date-range-form-field.component.scss'],
  standalone: false,
})
export class DateRangeFormFieldComponent extends DataTypeComponent implements OnDestroy, OnInit {
  @Input() formGroup: UntypedFormGroup;

  @Input() fieldConfig: FieldConfigDTO;

  @Input() data: DateRangeDTO;

  @Input() editMode: boolean;

  @Input() placeholder = false;

  public dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

  public saveDateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

  public modelDate: Moment[];

  matcher = new MyErrorStateMatcher();

  public timezone: string;

  public formattedDate: string;

  public timezoneSubscription: Subscription;

  internalFormGroup: UntypedFormGroup;

  toConfig: FieldConfigDTO;

  fromConfig: FieldConfigDTO;

  constructor(
    private systemService: SystemService,
    private changeDetectorRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder
  ) {
    super();
  }

  ngOnDestroy() {
    if (this.timezoneSubscription) {
      this.timezoneSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.systemService.systemTimeZoneSubject.subscribe((timezone: string) => {
      this.timezone = timezone;
      this.formatDate();
      this.onModelChange();
      this.changeDetectorRef.detectChanges();
    });

    if (typeof this.data === 'string' && this.data) {
      this.data = JSON.parse(this.data as unknown as string);
    }
    this.fromConfig = JSON.parse(JSON.stringify(this.fieldConfig));
    this.fromConfig.name = 'from';
    this.toConfig = JSON.parse(JSON.stringify(this.fieldConfig));
    this.toConfig.name = 'to';
    this.internalFormGroup = this.fb.group({
      from: this.data?.from,
      to: this.data?.to,
    });
    this.internalFormGroup.valueChanges.subscribe((c) => {
      this.formGroup.get(this.fieldConfig.name).setValue({
        from: c.from,
        to: c.to,
      });
    });
  }

  formatDate(): void {
    if (this.fieldConfig.dateFormat) {
      this.dateFormat = this.fieldConfig.dateFormat;
    }
    if (!this.data) {
      return;
    }

    const theData: DateRangeDTO = typeof this.data === 'string' ? JSON.parse(this.data) : this.data;
    this.modelDate = [moment(theData.from).tz(this.timezone), moment(theData.to).tz(this.timezone)];
    this.formattedDate = `${moment(theData.from).tz(this.timezone).format(this.dateFormat)} ~ ${moment(theData.to)
      .tz(this.timezone)
      .format(this.dateFormat)}`;

    if (this.formGroup && this.modelDate) {
      this.formGroup.get(this.fieldConfig.name).setValue({
        from: this.modelDate[0].format(this.saveDateFormat),
        to: this.modelDate[1].format(this.saveDateFormat),
      });
    }
  }

  onModelChange() {
    if (this.modelDate) {
      this.modelDate = this.modelDate.map((d) => d.tz(this.timezone));
    }

    if (this.formGroup && this.modelDate) {
      this.formGroup.get(this.fieldConfig.name).setValue({
        from: this.modelDate[0].format(this.saveDateFormat),
        to: this.modelDate[1].format(this.saveDateFormat),
      });
    }
  }

  condition(v: UiValidator) {
    const formControl = this.getFormControl();
    const b = !formControl.value || this.getValidatorConfig(v).condition(formControl);
    return b;
  }

  message(v: UiValidator) {
    return this.getValidatorConfig(v).message(v);
  }

  private getValidatorConfig(v: UiValidator): ValidatorConfig {
    return MyErrorStateMatcher.messageMap[v.type];
  }

  private getFormControl(): AbstractControl {
    return this.formGroup.get(this.fieldConfig.name);
  }
}
